import { useEffect, useState, useMemo } from "react";
import { api, API, eur, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PiggyBank, Plus, Download, Undo2, ArrowDownCircle, ArrowUpCircle, Lock, Trash2, RefreshCw, Banknote, Check, X } from "lucide-react";
import BankReconcile from "@/components/BankReconcile";

const TYPES = [
  "Bankopname", "Betaling klant", "Betaling leverancier", "Dagontvangsten contant",
  "Kasverschil", "Kost", "Privé opname", "Privé storting", "Transfer naar bank",
];
const today = () => new Date().toISOString().slice(0, 10);
const nlDate = (iso) => { try { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; } catch { return iso; } };

export default function Kasboek() {
  const { canWrite, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // setup form
  const [setup, setSetup] = useState({ name: "Centrale Kasboek Singh Express Shop", account_number: "BE63900000193308", begin_balance: "", begin_date: today(), begin_number: "169" });
  const [savingSetup, setSavingSetup] = useState(false);

  // add line
  const [addOpen, setAddOpen] = useState(false);
  const [line, setLine] = useState({ date: today(), direction: "in", amount: "", type: "", note: "", supplier_name: "", invoice_number: "" });
  const [saving, setSaving] = useState(false);

  // correction
  const [correctFor, setCorrectFor] = useState(null);
  const [correctNote, setCorrectNote] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/kasboek/lines").then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Contant terugbetaalde creditnota's (Colruyt & Grolli)
  const [creditProps, setCreditProps] = useState([]);
  const [propBusy, setPropBusy] = useState("");
  const loadProps = () => {
    api.get("/kasboek/credit-proposals").then((r) => setCreditProps(r.data.items || [])).catch(() => setCreditProps([]));
  };
  useEffect(() => { loadProps(); }, []);

  const acceptProp = async (p) => {
    setPropBusy(p.invoice_id);
    try {
      await api.post(`/kasboek/credit-proposals/${p.invoice_id}/accept`);
      toast.success(`Creditnota ${p.credit_note_number} contant geboekt in kasboek + factuurboek`);
      loadProps(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPropBusy(""); }
  };
  const dismissProp = async (p) => {
    setPropBusy(p.invoice_id);
    try {
      await api.post(`/kasboek/credit-proposals/${p.invoice_id}/dismiss`);
      loadProps();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPropBusy(""); }
  };

  const saveSetup = async () => {
    if (setup.begin_balance === "" || isNaN(parseFloat(setup.begin_balance))) return toast.error("Vul een geldig beginsaldo in");
    setSavingSetup(true);
    try {
      await api.post("/kasboek/config", {
        name: setup.name, account_number: setup.account_number,
        begin_balance: parseFloat(setup.begin_balance), begin_date: setup.begin_date,
        begin_number: parseInt(setup.begin_number || "1", 10),
      });
      toast.success("Kasboek gestart");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingSetup(false); }
  };

  const newSaldo = useMemo(() => {
    if (!data) return 0;
    const amt = parseFloat(line.amount) || 0;
    return Math.round((data.saldo + (line.direction === "in" ? amt : -amt)) * 100) / 100;
  }, [data, line]);

  const addLine = async () => {
    if (!line.type) return toast.error("Kies een type");
    if (!line.amount || parseFloat(line.amount) <= 0) return toast.error("Vul een bedrag groter dan 0 in");
    if (line.type === "Betaling leverancier" && !line.supplier_name.trim()) return toast.error("Vul de leveranciersnaam in");
    setSaving(true);
    try {
      await api.post("/kasboek/lines", {
        date: line.date, direction: line.direction, amount: parseFloat(line.amount),
        type: line.type, note: line.note,
        supplier_name: line.type === "Betaling leverancier" ? line.supplier_name.trim() : null,
        invoice_number: line.type === "Betaling leverancier" ? line.invoice_number.trim() : null,
      });
      toast.success("Kaslijn toegevoegd");
      setAddOpen(false);
      setLine({ date: today(), direction: "in", amount: "", type: "", note: "", supplier_name: "", invoice_number: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doCorrect = async () => {
    try {
      await api.post(`/kasboek/lines/${correctFor.id}/correct`, { note: correctNote });
      toast.success("Correctielijn toegevoegd");
      setCorrectFor(null); setCorrectNote("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [importing, setImporting] = useState(false);
  const importHistorical = async () => {
    setImporting(true);
    try {
      const r = await api.post("/kasboek/import-historical");
      if (r.data.already_imported) toast.info(`Historiek was al ingeladen (${r.data.legacy_count} lijnen)`);
      else toast.success(`Historiek ingeladen: ${r.data.imported} lijnen · eindsaldo € ${r.data.computed_end}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const [resyncing, setResyncing] = useState(false);
  const resyncHistorical = async () => {
    if (!window.confirm(
      "Kasboek-historiek hersynchroniseren met de geüploade bron-PDF (t/m 31/07/2026)?\n\n" +
      "• Alle overgenomen (historische) lijnen worden vervangen door de bron-PDF.\n" +
      "• Zelf toegevoegde lijnen (bv. via kasafstemming) blijven behouden.\n" +
      "• Het eindsaldo wordt herrekend volgens de bron-PDF.\n\nDoorgaan?"
    )) return;
    setResyncing(true);
    try {
      const r = await api.post("/kasboek/import-historical?resync=true");
      toast.success(`Historiek hersynchroniseerd: ${r.data.imported} lijnen · eindsaldo € ${r.data.computed_end}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setResyncing(false); }
  };

  const [exportOpen, setExportOpen] = useState(false);
  const [exportMode, setExportMode] = useState("full");
  const [expFrom, setExpFrom] = useState(data?.begin_date || "");
  const [expTo, setExpTo] = useState(today());
  const [exporting, setExporting] = useState(false);

  const exportPdf = async ({ from, to } = {}) => {
    setExporting(true);
    try {
      const token = localStorage.getItem("token");
      const qs = from && to ? `?from_date=${from}&to_date=${to}` : "";
      const res = await fetch(`${API}/kasboek/export${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("mislukt");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = from && to ? `Kasboek_${from}_${to}.pdf` : `Kasboek_${today()}.pdf`;
      a.click();
      toast.success("Kasboek geëxporteerd");
      setExportOpen(false);
    } catch (e) { toast.error("Kon kasboek niet exporteren"); }
    finally { setExporting(false); }
  };

  const [deleteFor, setDeleteFor] = useState(null);
  const doDelete = async () => {
    try {
      await api.delete(`/kasboek/lines/${deleteFor.id}`);
      toast.success("Lijn verwijderd");
      setDeleteFor(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div className="py-16 text-center text-muted-foreground">Laden…</div>;

  // ---------- Setup (begin saldo) ----------
  if (!data?.initialized) {
    return (
      <div className="max-w-xl space-y-6" data-testid="kasboek-setup">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
            <PiggyBank className="h-7 w-7 text-amber-600" /> Kasboek starten
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Geef het beginsaldo in waarmee u van uw vorige software vertrekt. Dit kan later niet meer gewijzigd worden zodra er lijnen geboekt zijn.
          </p>
        </div>
        <Card className="shadow-sm border-amber-300 bg-amber-50/40" data-testid="kasboek-import-card">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Download className="h-5 w-5 text-amber-600" /> Historisch kasboek overnemen</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Neem het volledige bestaande kasboek over (01/02/2023 → 31/07/2026, eindsaldo € 679,47). Alle lijnen, leveranciers en factuurnummers worden ingeladen; nieuwe ingaven vanaf 01/08/2026 lopen naadloos verder. De PDF-export blijft er identiek uitzien als voorheen.
            </p>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={importHistorical} disabled={importing || !canWrite} data-testid="kasboek-import-btn">
              {importing ? "Bezig met inladen…" : "Historiek overnemen"}
            </Button>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardHeader><CardTitle className="font-heading text-lg">Of: handmatig starten met beginsaldo</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5"><Label>Naam kasboek</Label>
              <Input data-testid="setup-name" value={setup.name} onChange={(e) => setSetup({ ...setup, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Rekeningnummer (kop van export)</Label>
              <Input data-testid="setup-account" value={setup.account_number} onChange={(e) => setSetup({ ...setup, account_number: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Beginsaldo (EUR)</Label>
                <Input type="number" step="0.01" data-testid="setup-balance" placeholder="0,00" value={setup.begin_balance} onChange={(e) => setSetup({ ...setup, begin_balance: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Begindatum</Label>
                <Input type="date" data-testid="setup-date" value={setup.begin_date} onChange={(e) => setSetup({ ...setup, begin_date: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Startnummer (afschriftnummer)</Label>
              <Input type="number" data-testid="setup-number" value={setup.begin_number} onChange={(e) => setSetup({ ...setup, begin_number: e.target.value })} />
              <p className="text-xs text-muted-foreground">Bv. 169 — elk volgend afschrift telt automatisch verder.</p></div>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={saveSetup} disabled={savingSetup || !canWrite} data-testid="setup-save">
              {savingSetup ? "Bezig…" : "Kasboek starten"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------- Main ----------
  return (
    <div className="space-y-6" data-testid="kasboek-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
            <PiggyBank className="h-7 w-7 text-amber-600" /> Kasboek
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{data.name}</p>
        </div>
        <div className="flex gap-3">
          {isAdmin && (
            <Button variant="outline" onClick={resyncHistorical} disabled={resyncing} data-testid="kasboek-resync">
              <RefreshCw className="h-4 w-4 mr-2" /> {resyncing ? "Bezig…" : "Historiek hersync"}
            </Button>
          )}
          <Button variant="outline" onClick={() => { setExpFrom(data.begin_date); setExpTo(today()); setExportMode("full"); setExportOpen(true); }} data-testid="kasboek-export"><Download className="h-4 w-4 mr-2" /> Exporteren (PDF)</Button>
        </div>
      </div>

      <Card className="shadow-sm max-w-sm border-amber-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-amber-600"><PiggyBank className="h-5 w-5" /><span className="font-heading font-semibold">{data.name}</span></div>
          <div className="text-sm text-muted-foreground mt-3">Laatste ingave: <span className="font-medium text-foreground">{nlDate(data.lines.length ? data.lines[data.lines.length - 1].date : data.begin_date)}</span></div>
          <div className="text-sm text-muted-foreground mt-1">Saldo: <span className="font-heading text-xl font-bold text-foreground tabular-nums">{eur(data.saldo)}</span></div>
          <Button className="mt-4 w-full bg-amber-600 hover:bg-amber-700" onClick={() => setAddOpen(true)} disabled={!canWrite} data-testid="kasboek-add-open">
            <Plus className="h-4 w-4 mr-2" /> Toevoegen
          </Button>
        </CardContent>
      </Card>

      {creditProps.length > 0 && (
        <Card className="shadow-sm border-emerald-300 bg-emerald-50/40" data-testid="credit-proposals">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Banknote className="h-5 w-5 text-emerald-600" /> Contant terug te betalen creditnota's
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Creditnota's van Colruyt en Grolli worden contant terugbetaald. Accepteer om ze als kasontvangst te boeken — dit boekt automatisch in kasboek + journaal en markeert de creditnota als betaald.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Datum</TableHead><TableHead>Leverancier</TableHead><TableHead>Creditnota</TableHead>
                <TableHead className="text-right">Bedrag</TableHead><TableHead className="text-right">Actie</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {creditProps.map((p) => (
                  <TableRow key={p.invoice_id} data-testid={`credit-prop-${p.invoice_id}`}>
                    <TableCell className="font-mono text-sm">{p.invoice_date ? nlDate(p.invoice_date) : "—"}</TableCell>
                    <TableCell>{p.supplier_name}</TableCell>
                    <TableCell className="text-muted-foreground">{p.credit_note_number || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium text-emerald-700">{eur(p.amount)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 mr-2" disabled={!canWrite || propBusy === p.invoice_id}
                        data-testid={`credit-accept-${p.invoice_id}`} onClick={() => acceptProp(p)}>
                        <Check className="h-4 w-4 mr-1" /> Contant boeken
                      </Button>
                      <Button size="sm" variant="ghost" disabled={!canWrite || propBusy === p.invoice_id}
                        data-testid={`credit-dismiss-${p.invoice_id}`} onClick={() => dismissProp(p)} title="Verbergen">
                        <X className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <BankReconcile onConfirmed={load} />


      <Card className="shadow-sm">
        <CardHeader><CardTitle className="font-heading text-lg">Historiek</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-12">NR</TableHead><TableHead>Datum</TableHead><TableHead>Omschrijving</TableHead>
              <TableHead className="text-right">Ontvangst</TableHead><TableHead className="text-right">Uitgave</TableHead>
              <TableHead className="text-right">Saldo</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              <TableRow className="bg-muted/40">
                <TableCell></TableCell><TableCell className="font-mono text-sm">{nlDate(data.begin_date)}</TableCell>
                <TableCell className="font-semibold">Beginsaldo {data.begin_number}</TableCell>
                <TableCell></TableCell><TableCell></TableCell>
                <TableCell className="text-right font-semibold tabular-nums">{eur(data.begin_balance)}</TableCell><TableCell></TableCell>
              </TableRow>
              {data.lines.map((l) => (
                <TableRow key={l.id} data-testid={`kas-line-${l.id}`} className={l.reversed ? "opacity-50" : ""}>
                  <TableCell className="text-muted-foreground">{l.nr}</TableCell>
                  <TableCell className="font-mono text-sm">{nlDate(l.date)}</TableCell>
                  <TableCell>
                    {l.is_correction ? <Badge variant="outline" className="text-red-600 border-red-300 mr-1">Correctie</Badge> : null}
                    {l.legacy ? <Badge variant="outline" className="text-muted-foreground border-border mr-1">Historiek</Badge> : null}
                    {l.is_correction ? l.note : l.type}
                    {!l.is_correction && l.supplier_name ? <span className="text-foreground"> · {l.supplier_name}</span> : null}
                    {!l.is_correction && l.invoice_number ? <span className="text-muted-foreground"> · Factuur: {l.invoice_number}</span> : null}
                    {!l.is_correction && l.note && !l.supplier_name ? <span className="text-muted-foreground"> · {l.note}</span> : null}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-emerald-700">{l.direction === "in" ? eur(l.amount) : ""}</TableCell>
                  <TableCell className="text-right tabular-nums text-red-600">{l.direction === "out" ? eur(l.amount) : ""}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{eur(l.balance)}</TableCell>
                  <TableCell className="text-right">
                    {canWrite && !l.is_correction && !l.reversed && !l.legacy && (
                      <Button variant="ghost" size="icon" title="Corrigeer" data-testid={`kas-correct-${l.id}`} onClick={() => setCorrectFor(l)}>
                        <Undo2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    )}
                    {canWrite && !l.is_correction && !l.legacy && (
                      <Button variant="ghost" size="icon" title="Verwijderen" data-testid={`kas-delete-${l.id}`} onClick={() => setDeleteFor(l)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                    {l.reversed && <span className="text-xs text-muted-foreground flex items-center gap-1 justify-end"><Lock className="h-3 w-3" /> gecorrigeerd</span>}
                  </TableCell>
                </TableRow>
              ))}
              {data.lines.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nog geen kaslijnen. Klik op "Toevoegen".</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add line dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Lijn toevoegen in {data.name}</DialogTitle>
            <DialogDescription>Laatste ingave: {nlDate(data.lines.length ? data.lines[data.lines.length - 1].date : data.begin_date)} · Huidig saldo: {eur(data.saldo)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Datum</Label>
              <Input type="date" data-testid="kas-date" value={line.date} min={data.begin_date} onChange={(e) => setLine({ ...line, date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Soort</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant={line.direction === "in" ? "default" : "outline"} className={line.direction === "in" ? "bg-emerald-600 hover:bg-emerald-700" : ""} data-testid="kas-dir-in" onClick={() => setLine({ ...line, direction: "in" })}>
                  <ArrowDownCircle className="h-4 w-4 mr-2" /> Ontvangst
                </Button>
                <Button type="button" variant={line.direction === "out" ? "default" : "outline"} className={line.direction === "out" ? "bg-red-600 hover:bg-red-700" : ""} data-testid="kas-dir-out" onClick={() => setLine({ ...line, direction: "out" })}>
                  <ArrowUpCircle className="h-4 w-4 mr-2" /> Uitgave
                </Button>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Type</Label>
              <Select value={line.type} onValueChange={(v) => setLine({ ...line, type: v, direction: v === "Betaling leverancier" ? "out" : line.direction })}>
                <SelectTrigger data-testid="kas-type"><SelectValue placeholder="Kies type" /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {line.type === "Betaling leverancier" && (
              <div className="grid grid-cols-2 gap-4 rounded-md border border-amber-200 bg-amber-50/60 p-3" data-testid="kas-supplier-fields">
                <div className="space-y-1.5"><Label>Leveranciersnaam</Label>
                  <Input data-testid="kas-supplier-name" placeholder="Bv. Jublou BV" value={line.supplier_name} onChange={(e) => setLine({ ...line, supplier_name: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Factuurnummer</Label>
                  <Input data-testid="kas-invoice-number" placeholder="Bv. V2600123 of Volgt nog" value={line.invoice_number} onChange={(e) => setLine({ ...line, invoice_number: e.target.value })} /></div>
              </div>
            )}
            <div className="space-y-1.5"><Label>Bedrag (EUR)</Label>
              <Input type="number" step="0.01" data-testid="kas-amount" placeholder="0,00" value={line.amount} onChange={(e) => setLine({ ...line, amount: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Opmerking</Label>
              <Textarea data-testid="kas-note" rows={2} value={line.note} onChange={(e) => setLine({ ...line, note: e.target.value })} /></div>
            <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">Nieuw saldo: <b className="tabular-nums" data-testid="kas-newsaldo">{eur(newSaldo)}</b></div>
          </div>
          <DialogFooter>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={addLine} disabled={saving} data-testid="kas-add-submit">
              {saving ? "Bezig…" : "Toevoegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Correction dialog */}
      <Dialog open={!!correctFor} onOpenChange={(o) => !o && setCorrectFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Lijn corrigeren</DialogTitle>
            <DialogDescription>Er wordt een onveranderlijke tegenboeking (CORRECTIE INGAVE) toegevoegd die deze lijn tenietdoet. De originele lijn blijft bewaard.</DialogDescription>
          </DialogHeader>
          {correctFor && (
            <div className="text-sm rounded-md border border-border p-3">
              <div>{nlDate(correctFor.date)} · {correctFor.type}</div>
              <div className="text-muted-foreground">{correctFor.direction === "in" ? "Ontvangst" : "Uitgave"} {eur(correctFor.amount)}</div>
            </div>
          )}
          <div className="space-y-1.5"><Label>Reden (optioneel)</Label>
            <Input data-testid="kas-correct-note" value={correctNote} onChange={(e) => setCorrectNote(e.target.value)} /></div>
          <DialogFooter>
            <Button variant="destructive" onClick={doCorrect} data-testid="kas-correct-submit"><Undo2 className="h-4 w-4 mr-2" /> Correctielijn toevoegen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Export dialog */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Kasboek exporteren</DialogTitle>
            <DialogDescription>Kies of u het volledige kasboek of een specifieke periode wilt exporteren.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant={exportMode === "full" ? "default" : "outline"} className={exportMode === "full" ? "bg-amber-600 hover:bg-amber-700" : ""} data-testid="export-mode-full" onClick={() => setExportMode("full")}>Volledig kasboek</Button>
              <Button type="button" variant={exportMode === "period" ? "default" : "outline"} className={exportMode === "period" ? "bg-amber-600 hover:bg-amber-700" : ""} data-testid="export-mode-period" onClick={() => setExportMode("period")}>Periode</Button>
            </div>
            {exportMode === "period" && (
              <div className="grid grid-cols-2 gap-4" data-testid="export-period-fields">
                <div className="space-y-1.5"><Label>Van datum</Label>
                  <Input type="date" data-testid="export-from" value={expFrom} min={data.begin_date} onChange={(e) => setExpFrom(e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Tot datum</Label>
                  <Input type="date" data-testid="export-to" value={expTo} onChange={(e) => setExpTo(e.target.value)} /></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button className="bg-amber-600 hover:bg-amber-700" disabled={exporting} data-testid="export-submit"
              onClick={() => exportPdf(exportMode === "period" ? { from: expFrom, to: expTo } : {})}>
              <Download className="h-4 w-4 mr-2" /> {exporting ? "Bezig…" : "Downloaden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Lijn verwijderen</DialogTitle>
            <DialogDescription>Deze kaslijn wordt definitief verwijderd. Enkel bedoeld om eigen (test)ingaven op te ruimen.</DialogDescription>
          </DialogHeader>
          {deleteFor && (
            <div className="text-sm rounded-md border border-border p-3">
              <div>{nlDate(deleteFor.date)} · {deleteFor.type}</div>
              <div className="text-muted-foreground">{deleteFor.direction === "in" ? "Ontvangst" : "Uitgave"} {eur(deleteFor.amount)}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="destructive" onClick={doDelete} data-testid="kas-delete-submit"><Trash2 className="h-4 w-4 mr-2" /> Definitief verwijderen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
